/**
 * @file sales-report.tsx
 * @module app/modules/finance-reports/sales-report
 * @description Displays the primary finance sales report with filter controls and paginated results.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Button, Card, Col, FormLabel, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Admin,
  AllModules,
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  OrdersDelivery,
  OutWardReports,
  PAGE_LIMIT,
  Piece,
  View,
} from "../../../utils/constants";
import APICallService from "../../../api/apiCallService";
import { multipleWarehouse, seller } from "../../../api/apiEndPoints";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { IMAGES } from "../../../utils/assetsAndPermissions";
import { error } from "../../../Global/toast";
import { useAuth } from "../auth";
import PermissionModal from "../../modals/permission";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { warehouse as warehouseStrings } from "../../../utils/storeString";
import { getKey, removeKey, setKey } from "../../../Global/history";
import { useWarehouse } from '../../context/WarehouseContext';

const SalesReport = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [details, setDetails] = useState<any>();
  const [type, setType] = useState<any>(true);
  const [endDate, setEndDate] = useState<any>(new Date());
  const [startDate, setStartDate] = useState<any>(new Date());
  const [fetchLoading, setFetchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [search, setSearch] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [orderData, setOrderData] = useState<any>([]);
  const [download, setDownload] = useState(false);
  const [salesData, setSalesData] = useState<any>([]);
  const [count, setCount] = useState({
    totalOrders: 0,
    totalAmount: 0,
    totalCommission: 0,
  });
  const [paymentDetails, setPaymentDetails] = useState<any>([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  useEffect(() => {
    (async () => {
      if (!Method.hasModulePermission(OutWardReports, currentUser)) {
        return window.history.back();
      }
      const warehouseValue = selectedWarehouse?.value || ""
      setFetchLoading(true);
      await fetchOrder(startDate, endDate, warehouseValue || null);
      setFetchLoading(false);
    })();
  }, []);
  const fetchOrder = async (
    startDate: string,
    endDate: string,
    warehouse?: any
  ) => {
    setCount({
      totalOrders: 0,
      totalAmount: 0,
      totalCommission: 0,
    });
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      utcOffset: new Date().getTimezoneOffset(),
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    let apiService = new APICallService(
      seller.orderSalesReport,
      params,
      "",
      "",
      "",
      "",
      OutWardReports,
      warehouse
    );
    let response = await apiService.callAPI();
    if (response) {
      setOrderData(response.ordersInfo);
      setSalesData(response.ordersSaleData);
      setCount({
        totalOrders: response.totalOrders,
        totalAmount: response.totalAmount,
        totalCommission: response.totalCommission,
      });
    }
  };
  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      setFetchLoading(true);
      setTotalRecords(0);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedWarehouse?._id || null
      );
      setFetchLoading(false);
    }
  };
  const handleDownload = async (startDate: string, endDate: string) => {
    setDownload(true);
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      download: true,
      utcOffset: new Date().getTimezoneOffset(),
    };
    if (selectedWarehouse?._id) {
      params = {
        ...params,
        warehouse: selectedWarehouse?._id || null,
      };
    }
    let apiService = new APICallService(
      seller.orderSalesReport,
      params,
      undefined,
      "arraybuffer",
      "",
      "",
      OutWardReports,
      selectedWarehouse?._id || null
    );
    let response = await apiService.callAPI();
    if (response) {
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "outward" + ".xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } else {
      error("Data Not Found");
    }
    setDownload(false);
  };

  return (
    <>
      {showPermissionModal && (
        <PermissionModal
          show={showPermissionModal}
          onHide={() => setShowPermissionModal(false)}
          moduleName={"orders & delivery"}
        />
      )}
      <div className="p-5">
        <Row className="g-4">
          <Col md>
            <div className="d-flex align-items-center mt-4">
              <h1 className="fs-22 fw-bolder">Outward Report</h1>
            </div>
          </Col>
          <Col md="auto">
            <div className="d-flex align-items-center">
              <FormLabel className="fs-16 fw-500">Filter by dates</FormLabel>
              <div className="ms-5">
                <CustomDatePicker
                  className="form-control bg-white min-h-30px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
                  onChange={handleChange}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  showFullMonthYearPicker={true}
                  maxDate={new Date()}
                  inputTextBG="bg-white"
                />
              </div>
            </div>
          </Col>
          <Col md="auto">
            <Button
              data-analytics-event="finance_reports_sales_report_download_excel_clicked"
              className="uw-btn uw-btn-primary fs-14 fw-600"
              onClick={() => handleDownload(startDate, endDate)}
              disabled={fetchLoading || download}
            >
              {!download && (
                <span className="indicator-label fs-16 fw-bold">
                  Download Excel
                </span>
              )}
              {download && (
                <span
                  className="indicator-progress fs-16 fw-bold"
                  style={{ display: "block" }}
                >
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </Button>
          </Col>
          <Col md="auto">
            <Button
              data-analytics-event="finance_reports_sales_report_toggle_view_clicked" className="uw-btn uw-btn-primary fs-14 fw-600" onClick={() => setType(!type)}>
              {!type ? `View Outward Report` : `Outward Grouped By Date`}
            </Button>
          </Col>
          <Row className="align-items-center mb-7 g-6">
            <Col xl={4} md={6} sm={4}>
              <div className="border-r8px bg-fbe5e5 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count.totalOrders > 0 ? count.totalOrders : 0}
                    </span>
                    <span className="fw-medium fs-16">{`Total Orders`} </span>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={4} md={6} sm={4}>
              <div className="border-r8px bg-c9f3d7 rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count.totalAmount > 0
                        ? "TSh " +
                          Method.getGeneralizedAmount(count.totalAmount)
                        : 0}
                    </span>
                    <span className="fw-medium fs-16">Total Sales Amount</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={4} md={6} sm={4}>
              <div className="border-r8px bg-fbeace rounded py-4 px-5">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column">
                    <span className="fs-22 fw-bolder">
                      {count.totalCommission > 0
                        ? "TSh " +
                          Method.getGeneralizedAmount(count.totalCommission)
                        : 0}
                    </span>
                    <span className="fw-medium fs-16">
                      Total Commission Earned
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Row>
        {type ? (
          <>
            <Card className="border border-r10px mt-6">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
                    <thead>
                      <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                        <th className="min-w-md-175px min-w-150px">
                          Order Date & ID
                        </th>
                        <th className="min-w-md-200px min-w-125px">
                          Customer name
                        </th>
                        <th className="min-w-md-125px">Quantity</th>
                        <th className="min-w-md-125px min-w-100px">
                          Total amount
                        </th>
                        <th className="min-w-md-125px min-w-100px">
                          Total commission
                        </th>
                        <th className="min-w-150px text-end"></th>
                      </tr>
                    </thead>
                    <tbody className="fs-15 fw-600">
                      {fetchLoading ? (
                        <>
                          <tr>
                            <td colSpan={5}>
                              <div className="d-flex justify-content-center">
                                <Loader loading={fetchLoading} />
                              </div>
                            </td>
                          </tr>
                        </>
                      ) : (
                        <>
                          {orderData.length ? (
                            <>
                              {" "}
                              {orderData.map((orderVal: any, index: number) => (
                                <>
                                  <tr key={index}>
                                    <td className="fs-15 fw-500">
                                      <div className="d-flex align-items-start flex-column">
                                        <div className="d-flex flex-row">
                                          <span className="text-dark d-block">
                                            {Method.convertDateToDDMMYYYYHHMM(
                                              orderVal._createdAt,
                                              "-"
                                            )}
                                          </span>
                                        </div>
                                        <span className="text-gray d-block">
                                          {orderVal.refKey}
                                        </span>
                                      </div>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500 d-block">
                                        {orderVal.customer.name}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        {/* {goodVal.quantity + ' items'} */}
                                        {orderVal.quantityTypes.reduce(
                                          (total: number, item: any) =>
                                            total + item.stockCount,
                                          0
                                        ) + " items"}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        {"TSh " +
                                          Method.getGeneralizedAmount(
                                            orderVal.payment.totalCharge
                                          )}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        {"TSh " +
                                          Method.getGeneralizedAmount(
                                            orderVal.commission
                                          )}
                                      </span>
                                    </td>
                                    <td className="text-end">
                                      {Method.hasPermission(
                                        OutWardReports,
                                        View,
                                        currentUser
                                      ) &&
                                      Method.hasPermission(
                                        OrdersDelivery,
                                        View,
                                        currentUser
                                      ) ? (
                                        <Button
                                          data-analytics-event={`finance_reports_sales_report_row_${index}_view_details_clicked`}
                                          className="uw-btn uw-btn-primary fs-14 fw-600"
                                          onClick={() =>
                                            navigate("/orders/order-details", {
                                              state: {
                                                id: orderVal._id,
                                                moduleName: OutWardReports,
                                              },
                                            })
                                          }
                                        >
                                          View details
                                        </Button>
                                      ) : (
                                        <></>
                                      )}
                                      {Method.hasPermission(
                                        OutWardReports,
                                        View,
                                        currentUser
                                      ) &&
                                      !Method.hasPermission(
                                        OrdersDelivery,
                                        View,
                                        currentUser
                                      ) ? (
                                        <Button
                                          data-analytics-event={`finance_reports_sales_report_row_${index}_view_details_clicked`}
                                          className="uw-btn uw-btn-primary fs-14 fw-600"
                                          onClick={() => {
                                            setShowPermissionModal(true);
                                          }}
                                        >
                                          View details
                                        </Button>
                                      ) : (
                                        <></>
                                      )}
                                    </td>
                                  </tr>
                                </>
                              ))}
                            </>
                          ) : (
                            <tr>
                              <td colSpan={5}>
                                <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                  No Data found
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : (
          <>
            <Card className="border border-r10px mt-6">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
                    <thead>
                      <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                        <th className="min-w-md-175px min-w-150px">Date</th>
                        <th className="min-w-md-200px min-w-125px">
                          Total Orders
                        </th>
                        <th className="min-w-md-125px">Total Sales</th>
                        <th className="min-w-md-125px">
                          Total Commission Earned
                        </th>
                      </tr>
                    </thead>
                    <tbody className="fs-15 fw-600">
                      {fetchLoading ? (
                        <>
                          <tr>
                            <td colSpan={5}>
                              <div className="d-flex justify-content-center">
                                <Loader loading={fetchLoading} />
                              </div>
                            </td>
                          </tr>
                        </>
                      ) : (
                        <>
                          {salesData.length ? (
                            <>
                              {" "}
                              {salesData.map((orderVal: any, index: number) => (
                                <>
                                  <tr key={index}>
                                    <td className="fs-15 fw-500">
                                      <span className="fs-15 fw-500 d-block">
                                        {Method.convertDateToDDMMYYYY(
                                          orderVal.date
                                        )}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500 d-block">
                                        {Method.getGeneralizedAmount(
                                          orderVal.totalOrders
                                        )}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="fs-15 fw-500">
                                        {"TSh " +
                                          Method.getGeneralizedAmount(
                                            orderVal.totalSales
                                          )}
                                      </span>
                                    </td>
                                    <td className="fs-15 fw-500">
                                      <span className="fs-15 fw-500 d-block">
                                        {"TSh " +
                                          Method.getGeneralizedAmount(
                                            orderVal.totalCommission
                                          )}
                                      </span>
                                    </td>
                                  </tr>
                                </>
                              ))}
                            </>
                          ) : (
                            <tr>
                              <td colSpan={5}>
                                <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                  No Data found
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </>
  );
};
export default SalesReport;
